import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Badge } from '@/components/ui/PageHeader';
import { getProviderCategories } from '@/api/providers';
import { growth, type LocationDraft, type LocationFields, type LocationMatch } from '@/api/provider-growth';
import { useGrowthCommand } from './useGrowthCommand';
import { useAuth } from '@/context/AuthContext';

const blank = (): LocationFields => ({
  name: '', categoryCode: '', city: '', state: '', description: '', address: '', phone: '', kind: 'business',
});
function fieldsFor(draft: LocationDraft): LocationFields {
  return { name: draft.name, categoryCode: draft.category.code, city: draft.city, state: draft.state,
    description: draft.description ?? '', address: draft.address ?? '', phone: draft.phone ?? '',
    ...(draft.website ? { website: draft.website } : {}), kind: draft.kind };
}
export function ProviderDraftsPanel() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['growth-drafts', user?.id], queryFn: growth.drafts, retry: false });
  const categories = useQuery({ queryKey: ['provider-categories'], queryFn: getProviderCategories });
  const command = useGrowthCommand();
  const [draft, setDraft] = useState<LocationDraft | null>(null);
  const [fields, setFields] = useState<LocationFields>(blank);
  const [matches, setMatches] = useState<LocationMatch[] | null>(null);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [acceptingWork, setAcceptingWork] = useState(false);
  const editable = !draft || ['draft', 'changes_requested'].includes(draft.lifecycleStatus);
  const dirty = !!draft && JSON.stringify(fields) !== JSON.stringify(fieldsFor(draft));
  const checked = matches !== null && matches.every((match) => acknowledged.includes(match.id));

  function adopt(value: LocationDraft | null) {
    setDraft(value); setFields(value ? fieldsFor(value) : blank()); setMatches(null);
    setAcknowledged([]); setConfirmPublish(false); setAcceptingWork(false);
  }
  function change<K extends keyof LocationFields>(key: K, value: LocationFields[K]) {
    setFields((old) => ({ ...old, [key]: value })); setMatches(null); setAcknowledged([]);
  }
  async function load(id: string) {
    const response = await command.run('load-location', id, () => growth.draft(id));
    if (response) adopt(response.data);
  }
  async function checkMatches() {
    const response = await command.run('check-matches', fields, () => growth.matches(fields));
    if (response) { setMatches(response.data.matches); setAcknowledged([]); }
  }
  async function save() {
    const response = await command.run('save-location', { id: draft?.id, revision: draft?.revision, fields, acknowledged },
      (idempotencyKey) => draft
        ? growth.updateDraft(draft.id, fields, { idempotencyKey, expectedRevision: draft.revision })
        : growth.createDraft(fields, idempotencyKey, acknowledged));
    if (response) { adopt(response.data); command.setMessage('Private draft saved. Nothing is published.'); await query.refetch(); }
  }
  async function advance(publish: boolean) {
    if (!draft || dirty) return;
    const response = await command.run(publish ? 'publish-location' : 'submit-location',
      { id: draft.id, revision: draft.revision, acknowledged, acceptingWork },
      (idempotencyKey) => publish
        ? growth.publishDraft(draft.id, { idempotencyKey, expectedRevision: draft.revision }, acceptingWork)
        : growth.submitDraft(draft.id, { idempotencyKey, expectedRevision: draft.revision }, acknowledged));
    if (response) {
      adopt(response.data);
      command.setMessage(publish ? 'Location published.' : 'Location submitted for review.');
      await query.refetch(); await qc.invalidateQueries({ queryKey: ['my-providers'] });
    }
  }
  async function discard() {
    if (!draft || !window.confirm(`Discard the draft "${draft.name}"? It cannot be brought back.`)) return;
    const response = await command.run('discard-location', { id: draft.id, revision: draft.revision },
      (idempotencyKey) => growth.discardDraft(draft.id, { idempotencyKey, expectedRevision: draft.revision }));
    if (response) {
      adopt(null);
      command.setMessage('Draft discarded. It is in no list and was never public.');
      await query.refetch();
    }
  }
  return <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4" aria-label="Reviewed Provider locations">
    <h2 className="font-semibold">Reviewed locations</h2>
    <p className="text-sm text-gray-600">Create a private location draft, check existing listings, then submit it for review. Only an approved location can be published. Claims still use the existing claim flow.</p>
    {query.isLoading && <p role="status">Loading your drafts…</p>}
    {query.error && <p role="alert">{query.error.message} <Button onClick={() => void query.refetch()}>Retry drafts</Button></p>}
    <fieldset disabled={command.busy} className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => adopt(null)}>New location draft</Button>
        {(query.data?.data.drafts ?? []).map((item) =>
          <Button variant="secondary" key={item.id} onClick={() => void load(item.id)}>{item.name} · {item.lifecycleStatus}</Button>)}
      </div>
      {draft && <p className="text-sm"><Badge variant="gray">{draft.lifecycleStatus}</Badge> Revision {draft.revision} · {draft.id}</p>}
      {draft?.reviewMessage && <p role="status" className="text-sm">Reviewer: {draft.reviewMessage}</p>}
      <fieldset disabled={!editable} className="grid sm:grid-cols-2 gap-3">
        {(['name', 'city', 'state', 'address', 'phone'] as const).map((key) =>
          <label key={key} className="text-sm capitalize">{key}<input className="input mt-1" value={fields[key]} onChange={(event) => change(key, event.target.value)} /></label>)}
        <label className="text-sm">Category<select className="input mt-1" value={fields.categoryCode} onChange={(event) => change('categoryCode', event.target.value)}>
          <option value="">Choose a category</option>
          {(categories.data?.data ?? []).map((category) => <option key={category.code} value={category.code}>{category.name}</option>)}
        </select></label>
        <label className="text-sm">Provider type<select className="input mt-1" value={fields.kind} onChange={(event) => change('kind', event.target.value as LocationFields['kind'])}>
          <option value="business">Business</option><option value="individual">Independent provider</option>
        </select></label>
        <label className="text-sm">Website<input type="url" className="input mt-1" value={fields.website ?? ''} onChange={(event) => change('website', event.target.value || undefined)} /></label>
        <label className="text-sm sm:col-span-2">Description<textarea className="input mt-1" rows={3} value={fields.description} onChange={(event) => change('description', event.target.value)} /></label>
      </fieldset>
      {categories.error && <p role="alert">Categories failed to load. <Button onClick={() => void categories.refetch()}>Retry categories</Button></p>}
      {editable && <>
        <Button variant="secondary" disabled={!fields.name.trim() || !fields.city.trim() || !fields.state.trim()} onClick={() => void checkMatches()}>Check existing listings</Button>
        {matches !== null && <div className="space-y-2 text-sm">
          {matches.length === 0 && <p>No matching listings returned for these details.</p>}
          {matches.map((match) => <label className="flex gap-2" key={match.id}>
            <input type="checkbox" checked={acknowledged.includes(match.id)} onChange={(event) =>
              setAcknowledged((old) => event.target.checked ? [...old, match.id] : old.filter((id) => id !== match.id))} />
            I checked {match.name} ({match.city}, {match.state}) and this is a separate location.
          </label>)}
        </div>}
        <div className="flex gap-2 flex-wrap">
          <Button disabled={!fields.categoryCode || !fields.name.trim() || !fields.city.trim() || !fields.state.trim() || (!draft && !checked)} onClick={() => void save()}>Save private location</Button>
          {draft && <Button disabled={dirty || !checked} onClick={() => void advance(false)}>Submit location for review</Button>}
          {draft?.lifecycleStatus === 'draft' && !draft.submittedAt && <Button variant="danger" onClick={() => void discard()}>Discard this draft</Button>}
        </div>
        {dirty && <p className="text-sm">Save edits, then check matching listings before submission.</p>}
      </>}
      {draft?.lifecycleStatus === 'approved' && <div className="space-y-2">
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={acceptingWork} onChange={(event) => setAcceptingWork(event.target.checked)} />This location is accepting work.</label>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={confirmPublish} onChange={(event) => setConfirmPublish(event.target.checked)} />I confirm this approved location should become public in the connected environment.</label>
        <Button disabled={!confirmPublish} onClick={() => void advance(true)}>Publish approved location</Button>
      </div>}
      {draft && draft.lifecycleStatus !== 'published' && <Button variant="secondary" onClick={() => void load(draft.id)}>Reload saved location</Button>}
    </fieldset>
    {command.error && <p role="alert" className="text-sm text-red-700">{command.error} Your edits are kept. Reload if the saved revision changed.</p>}
    {command.message && <p role="status" className="text-sm text-green-700">{command.message}</p>}
  </section>;
}
